import { useEffect, useMemo, useRef, useState } from 'react'

import { validateDoc, validationIssues, type RegistryKind } from '@/api/actions'
import { isApiError } from '@/api/client'

import { asList, asObj, ID_RE, ROLE_RE, type Obj } from './doc'
import type { Issue } from './issues'

/**
 * Live checking while you edit: a moment after the last change the draft goes to
 * `POST /api/{actions|rules}/validate` (always 200 `{ok, errors, warnings}`); an older answer is
 * dropped when a newer draft exists. A server without the endpoint (404/405) is not asked again —
 * the editor then checks the basics itself and the real check happens on save.
 */

export type CheckStatus = 'checking' | 'ok' | 'invalid' | 'offline'

export interface LiveCheck {
  status: CheckStatus
  errors: Issue[]
  warnings: Issue[]
}

interface Answer {
  key: string
  errors: Issue[]
  warnings: Issue[]
  offline: boolean
}

/** `savedId`: a stored document's id (its `PUT` path), so changing the id in the JSON shows at once. */
export function useLiveValidation(kind: RegistryKind, draft: Obj, mode: 'edit' | 'new', delay = 450, savedId?: string): LiveCheck {
  const key = useMemo(() => JSON.stringify(draft), [draft])
  const [answer, setAnswer] = useState<Answer | null>(null)
  const missing = useRef(false)
  const draftRef = useRef(draft)
  draftRef.current = draft

  useEffect(() => {
    const ctrl = new AbortController()
    const t = setTimeout(
      () => {
        if (missing.current) {
          setAnswer({ key, errors: [], warnings: [], offline: true })
          return
        }
        validateDoc(kind, draftRef.current, ctrl.signal, savedId).then(
          (r) => setAnswer({ key, errors: r.errors, warnings: r.warnings, offline: false }),
          (err: unknown) => {
            if (ctrl.signal.aborted) return
            if (isApiError(err) && err.status === 422) {
              // A server that answers bad drafts with 422 still tells us what is wrong.
              setAnswer({ key, errors: validationIssues(err), warnings: [], offline: false })
              return
            }
            if (isApiError(err) && (err.status === 404 || err.status === 405 || err.status === 501)) missing.current = true
            setAnswer({ key, errors: [], warnings: [], offline: true })
          },
        )
      },
      missing.current ? 0 : delay,
    )
    return () => {
      clearTimeout(t)
      ctrl.abort()
    }
  }, [kind, key, delay, savedId])

  return useMemo((): LiveCheck => {
    if (answer?.offline) {
      const local = localIssues(kind, draft, mode)
      return { status: answer.key !== key ? 'checking' : local.length ? 'invalid' : 'offline', errors: local, warnings: [] }
    }
    const status: CheckStatus = !answer || answer.key !== key ? 'checking' : answer.errors.length ? 'invalid' : 'ok'
    return { status, errors: answer?.errors ?? [], warnings: answer?.warnings ?? [] }
  }, [answer, key, kind, draft, mode])
}

/** The checks the editor can make on its own (when the server can't check a draft). */
export function localIssues(kind: RegistryKind, doc: Obj, mode: 'edit' | 'new'): Issue[] {
  const out: Issue[] = []
  if (mode === 'new' && !ID_RE.test(String(doc.id ?? ''))) out.push({ path: 'id', message: 'Give it a name: at least 2 letters (a–z, 0–9 and _), starting with a letter.' })
  if (kind === 'action') {
    if (!String(doc.label ?? '').trim()) out.push({ path: 'label', message: 'Give it a name.' })
    const roles = asObj(doc.roles)
    const names = Object.keys(roles)
    if (names.length === 0) out.push({ path: 'roles', message: 'Add at least one participant.' })
    else if (!names.some((n) => asObj(roles[n]).required !== false)) out.push({ path: 'roles', message: 'At least one participant must be there (switch on “Must be there”).' })
    for (const n of names) if (!ROLE_RE.test(n)) out.push({ path: `roles.${n}`, message: `“${n}” can’t be a name: use 2–25 letters, numbers or _, starting with a letter.` })
    const program = asList(doc.program)
    if (program.length === 0) out.push({ path: 'program', message: 'Add at least one step.' })
    program.forEach((s, i) => {
      for (const r of Object.keys(asObj(asObj(s).tracks))) if (!names.includes(r)) out.push({ path: `program.${i}.tracks`, message: `“${r}” is not one of the participants.` })
    })
  } else {
    if (!asObj(doc.when).event) out.push({ path: 'when.event', message: 'Pick what happens.' })
    if (asList(doc.then).length === 0 && asList(doc.result).length === 0) out.push({ path: 'then', message: 'Add a step, or a change under Afterwards.' })
  }
  return out
}
